import { RefreshCw } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { MailAccount, MailboxInput } from "./mailTypes";
import { INFOMANIAK_DEFAULTS } from "./mailTypes";
import { formatRelativeDate } from "../misc/RelativeDate";
import { MailStatus } from "./MailStatus";
import { useMailAccountMutation } from "./useMailAccount";

/** « Boîte mail » card: connects the user's Infomaniak mailbox (IMAP + SMTP). */
export const MailboxSection = ({
  account,
}: {
  account: MailAccount | null;
}) => {
  const translate = useTranslate();
  const isConnected =
    !!account?.email && account.mail_status !== "disconnected";
  const [email, setEmail] = useState(account?.email ?? "");
  const [password, setPassword] = useState("");
  const [servers, setServers] = useState({
    imap_host: account?.imap_host ?? INFOMANIAK_DEFAULTS.imap_host,
    imap_port: String(account?.imap_port ?? INFOMANIAK_DEFAULTS.imap_port),
    smtp_host: account?.smtp_host ?? INFOMANIAK_DEFAULTS.smtp_host,
    smtp_port: String(account?.smtp_port ?? INFOMANIAK_DEFAULTS.smtp_port),
  });

  const connect = useMailAccountMutation(
    (dataProvider, input: MailboxInput) => dataProvider.connectMailbox(input),
    "crm.mail.mailbox.saved",
  );
  const disconnect = useMailAccountMutation(
    (dataProvider) => dataProvider.disconnectMailbox(),
    "crm.mail.mailbox.disconnected",
  );
  const sync = useMailAccountMutation(
    (dataProvider) => dataProvider.syncMailbox(),
    "crm.mail.mailbox.sync_started",
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    connect.mutate(
      {
        email: email.trim(),
        password: password || undefined,
        imap_host: servers.imap_host.trim(),
        imap_port: Number(servers.imap_port),
        smtp_host: servers.smtp_host.trim(),
        smtp_port: Number(servers.smtp_port),
      },
      { onSuccess: () => setPassword("") },
    );
  };

  const serverField = (name: keyof typeof servers) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`mail-${name}`}>
        {translate(`crm.mail.mailbox.${name}`)}
      </Label>
      <Input
        id={`mail-${name}`}
        value={servers[name]}
        onChange={(event) =>
          setServers({ ...servers, [name]: event.target.value })
        }
      />
    </div>
  );

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">
              {translate("crm.mail.mailbox.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isConnected
                ? translate("crm.mail.mailbox.connected_as", {
                    email: account?.email,
                  })
                : translate("crm.mail.mailbox.help")}
            </p>
          </div>
          <MailStatus
            kind="mailbox"
            status={account?.mail_status ?? "disconnected"}
            error={account?.mail_error}
          />
        </div>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mail-email">
                {translate("crm.mail.mailbox.email")}
              </Label>
              <Input
                id="mail-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mail-password">
                {translate("crm.mail.mailbox.password")}
              </Label>
              <Input
                id="mail-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={
                  isConnected
                    ? translate("crm.mail.mailbox.password_keep")
                    : undefined
                }
              />
            </div>
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              {translate("crm.mail.mailbox.advanced")}
            </summary>
            <div className="grid grid-cols-2 gap-4 pt-3">
              {serverField("imap_host")}
              {serverField("imap_port")}
              {serverField("smtp_host")}
              {serverField("smtp_port")}
            </div>
          </details>
          {account?.mail_status === "connected" ? (
            <SyncStatus
              account={account}
              isSyncing={sync.isPending}
              onSync={() => sync.mutate(undefined)}
            />
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            {isConnected ? (
              <Button
                type="button"
                variant="outline"
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate(undefined)}
              >
                {translate("crm.mail.disconnect")}
              </Button>
            ) : null}
            <Button type="submit" disabled={connect.isPending}>
              {translate(
                connect.isPending
                  ? "crm.mail.checking"
                  : isConnected
                    ? "crm.mail.mailbox.update"
                    : "crm.mail.mailbox.connect",
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

/** When the emails were last read, and a button to read them now. */
const SyncStatus = ({
  account,
  isSyncing,
  onSync,
}: {
  account: MailAccount;
  isSyncing: boolean;
  onSync: () => void;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 p-3 text-sm">
      <span className="flex-1 text-muted-foreground">
        {!account.history_synced_at || !account.last_sync_at
          ? translate("crm.mail.mailbox.history_running")
          : translate("crm.mail.mailbox.last_sync", {
              date: formatRelativeDate(account.last_sync_at, locale),
            })}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isSyncing}
        onClick={onSync}
      >
        <RefreshCw className="size-4" />
        {translate("crm.mail.mailbox.sync_now")}
      </Button>
    </div>
  );
};
